import { BYTES_PER_MB, UPLOAD_FAILURE_TOAST_AUTO_HIDE_DURATION_MS } from "@/components/upload/constants"
import { isFileTypeAllowed } from "@/components/upload/utils"
import { NotificationType } from "@/schemas/notification"

export enum FileRejectionReason {
  TooLarge = "too-large",
  UnsupportedType = "unsupported-type",
  NameTooLong = "name-too-long",
  Duplicate = "duplicate",
}

export interface FileRejection {
  file: File
  reason: FileRejectionReason
}

export interface OverLimitRejection {
  maxFiles: number
  currentCount: number
  incomingCount: number
}

export interface PartitionFilesForUploadResult {
  accepted: File[]
  rejections: FileRejection[]
  overLimit?: OverLimitRejection
}

interface PartitionFilesForUploadParams {
  incomingFiles: File[]
  pendingFiles: File[]
  existingFileNames?: string[]
  maxFiles: number
  maxSizeBytes: number
  maxFileNameLength: number
  allowedMimeTypes: string[]
}

const toDedupeKey = (fileName: string, fileSize: number): string => `${fileName.toLowerCase()}_${fileSize}`

export const partitionFilesForUpload = ({
  incomingFiles,
  pendingFiles,
  existingFileNames = [],
  maxFiles,
  maxSizeBytes,
  maxFileNameLength,
  allowedMimeTypes,
}: PartitionFilesForUploadParams): PartitionFilesForUploadResult => {
  const currentCount = pendingFiles.length + existingFileNames.length

  if (currentCount + incomingFiles.length > maxFiles) {
    return {
      accepted: [],
      rejections: [],
      overLimit: { maxFiles, currentCount, incomingCount: incomingFiles.length },
    }
  }

  const existingNamesLower = new Set(existingFileNames.map((name) => name.toLowerCase()))
  const seenDedupeKeys = new Set(pendingFiles.map((file) => toDedupeKey(file.name, file.size)))

  const accepted: File[] = []
  const rejections: FileRejection[] = []

  for (const file of incomingFiles) {
    if (file.size > maxSizeBytes) {
      rejections.push({ file, reason: FileRejectionReason.TooLarge })
      continue
    }
    if (!isFileTypeAllowed(file, allowedMimeTypes)) {
      rejections.push({ file, reason: FileRejectionReason.UnsupportedType })
      continue
    }
    if (file.name.length > maxFileNameLength) {
      rejections.push({ file, reason: FileRejectionReason.NameTooLong })
      continue
    }

    const dedupeKey = toDedupeKey(file.name, file.size)
    if (seenDedupeKeys.has(dedupeKey) || existingNamesLower.has(file.name.toLowerCase())) {
      rejections.push({ file, reason: FileRejectionReason.Duplicate })
      continue
    }

    seenDedupeKeys.add(dedupeKey)
    accepted.push(file)
  }

  return { accepted, rejections }
}

export const getOverLimitMessage = ({ maxFiles, currentCount, incomingCount }: OverLimitRejection): string => {
  const overBy = currentCount + incomingCount - maxFiles
  return `You can attach up to ${maxFiles} files. You have ${currentCount} — remove ${overBy}, or drop 1 file at a time.`
}

const getRejectionReasonToLabel = (
  maxSizeBytes: number,
  maxFileNameLength: number
): Record<FileRejectionReason, string> => ({
  [FileRejectionReason.TooLarge]: `Too large (max ${Math.round(maxSizeBytes / BYTES_PER_MB)}MB)`,
  [FileRejectionReason.UnsupportedType]: "Unsupported type",
  [FileRejectionReason.NameTooLong]: `Name too long (max ${maxFileNameLength} characters)`,
  [FileRejectionReason.Duplicate]: "Already attached",
})

export const getRejectionsMessage = (
  rejections: FileRejection[],
  { maxSizeBytes, maxFileNameLength }: { maxSizeBytes: number; maxFileNameLength: number }
): string | null => {
  if (!rejections.length) return null

  const reasonToLabel = getRejectionReasonToLabel(maxSizeBytes, maxFileNameLength)
  const reasonToFileNames = new Map<FileRejectionReason, string[]>()

  for (const { file, reason } of rejections) {
    const fileNames = reasonToFileNames.get(reason) ?? []
    fileNames.push(file.name)
    reasonToFileNames.set(reason, fileNames)
  }

  const lines = Array.from(reasonToFileNames.entries()).map(
    ([reason, fileNames]) => `${reasonToLabel[reason]}: ${fileNames.join(", ")}`
  )

  const fileCountLabel = rejections.length === 1 ? "1 file wasn't added" : `${rejections.length} files weren't added`
  return `${fileCountLabel}:\n${lines.join("\n")}`
}

export interface UploadFailure {
  id: string
  fileName: string
}

export const getUploadFailureMessage = (failures: UploadFailure[]): string => {
  const fileCountLabel =
    failures.length === 1 ? "1 document wasn't attached" : `${failures.length} documents weren't attached`
  return `${fileCountLabel}: ${failures.map((failure) => failure.fileName).join(", ")}`
}

export interface UploadOutcomeNotification {
  type: NotificationType
  message: string
  autoHideDuration?: number
}

export const getUploadOutcomeNotification = (
  failures: UploadFailure[],
  itemCount: number,
  totalFailureMessage?: string
): UploadOutcomeNotification | undefined => {
  if (!itemCount || !failures.length) return undefined

  if (failures.length === itemCount) {
    return totalFailureMessage ? { type: NotificationType.Error, message: totalFailureMessage } : undefined
  }

  return {
    type: NotificationType.Error,
    message: getUploadFailureMessage(failures),
    autoHideDuration: UPLOAD_FAILURE_TOAST_AUTO_HIDE_DURATION_MS,
  }
}

export const getFieldFile = (field: object): File => (field as unknown as { file: File }).file
